import type {
  AppEntry,
  ShopConfig,
} from "../../pina-server/node_modules/@prisma/client";
import { BADGE_ID, BUTTON_ID } from "./components";
import ShopComponent from "./components/ShopComponent.svelte";
import { footprintPrice, modalOpen } from "./components/store";
import {
  type FootprintResponse,
  type IShopSystemStrategy,
} from "./lib/ShopSystemStrategy/IShopSystemStrategy";
import { capturePHEvent, type PostHogEvent } from "./lib/posthog";
import { registerSessionConfigAction } from "./sessionConfig";
import type { DispatcherEvent } from "./types";

const MOBILE_BREAKPOINT = 762;

export class App {
  shopSystemStrategy: IShopSystemStrategy;

  // The current footprint calculated for the user's cart.
  footprint: FootprintResponse = {
    price: null,
    totalFootprint: null,
    orderValueWithoutOffset: null,
    inCart: false,
  };

  //TODO: remove this temp fix
  hasModal = false;

  // The currently loaded svelte components.
  components: ShopComponent[] = [];

  constructor(shopSystemStrategy: IShopSystemStrategy) {
    this.shopSystemStrategy = shopSystemStrategy;
  }

  async load(page: string) {
    const config = await this.shopSystemStrategy.getConfig(page);

    if (!config) {
      console.error("No config found for this page");
      return;
    }

    // Reinject svelte components if the route changes.
    window.addEventListener("load", () => this.observeUrlChange(config));

    // Add general handlers.
    this.registerEventHandlers(config);
    // Add shop system specific handlers.
    this.shopSystemStrategy.registerEventHandlers();

    // Wait until page is ready to receive the elements
    await this.shopSystemStrategy.loadingDone();

    config.appEntries.forEach((entry: any) => {
      this.renderElements(entry);
    });

    registerSessionConfigAction((config) => {
      // Offset the first opportunity the user has, and then stop.
      if (!config.optIn && !sessionStorage.getItem("pina-opt-out")) {
        const unsubscribe = footprintPrice.subscribe((price) => {
          if (price > 0) {
            this.shopSystemStrategy.toggleOffset(true);
            sessionStorage.setItem("pina-opt-out", "set");
            unsubscribe();
          }
        });
      }
    });

    capturePHEvent("pageview");
  }

  lastWidth = window.innerWidth;

  registerEventHandlers(config: ShopConfig & { appEntries: AppEntry[] }) {
    if (window.location.hostname.startsWith("mozart-bett.de")) {
      window.addEventListener("openModal", () => {
        modalOpen.set(true);
        capturePHEvent("modal_opened_by_badge");
      });
    } else {
      window.addEventListener("pinaOpenModal", () => {
        modalOpen.set(true);
        capturePHEvent("modal_opened_by_badge");
      });
    }

    window.addEventListener("bannerDisplayed", () => {
      modalOpen.set(true);
      capturePHEvent("banner_displayed");
    });

    window.addEventListener("phEvent", (ev) => {
      if (
        ev instanceof CustomEvent &&
        ev.detail &&
        typeof ev.detail === "string"
      )
        capturePHEvent(ev.detail as PostHogEvent);
    });

    // reinit app after resize because of potential differences in selector and selectorMobile
    // TODO: make sure mobile is deleted after switching to desktop and viseversa
    let timeout: NodeJS.Timeout | number;
    window.addEventListener("resize", () => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        const unchangedWidth = this.lastWidth == window.innerWidth;
        this.lastWidth = window.innerWidth;
        // This guards against mobile browser resizes when the address bar is shown/hidden.
        if (unchangedWidth) return;
        config?.appEntries.forEach((entry: any) => this.renderElements(entry));
      }, 200);
    });

    window.addEventListener("PriceChanged", () => {
      this.updateFootprint();
    });

    window.addEventListener("CartReRendered", () => {
      this.updateFootprint();
      config?.appEntries
        .filter((entry: any) => ["cart", "checkout"].includes(entry.pageType))
        .forEach((entry: any) => this.renderElements(entry));
    });
  }

  async updateFootprint() {
    if (this.footprint.price !== null) {
      this.footprint.price = 0;
    }
    const data: FootprintResponse | undefined =
      await this.shopSystemStrategy.getFootprint();
    if (data) {
      // If these do not match, it means the change did not come from our function.
      if (!data.inCart && this.footprint.inCart)
        capturePHEvent("offset_removed_from_cart", { manual: true });
      this.footprint = data;
      footprintPrice.set(this.footprint.price ?? 0);

      const priceWithoutFootprint = data.inCart
        ? (this.footprint.orderValueWithoutOffset ?? 0) -
          (this.footprint?.price ?? 0)
        : this.footprint.orderValueWithoutOffset ?? 0;

      window.sessionStorage.setItem(
        "pina-order-value-without-offset",
        priceWithoutFootprint.toString(),
      );
      window.sessionStorage.setItem(
        "pina-footprint-co2",
        this.footprint.totalFootprint?.toString() ?? "",
      );
      window.sessionStorage.setItem(
        "pina-footprint-price",
        this.footprint?.price?.toString() ?? "",
      );

      if (this.components.length > 0) {
        this.components.forEach((component) => {
          component.componentProps = {
            ...component.componentProps,
            ...this.footprint,
          };
        });
      }
    }
  }

  async toggleOffset(event: any) {
    const active = event.detail.active;

    capturePHEvent(active ? "offset_selected" : "offset_deselected");

    const ticks = performance.now();
    const stateChanged = await this.shopSystemStrategy.toggleOffset(active);
    const time = performance.now() - ticks;

    if (stateChanged) {
      capturePHEvent(
        active ? "offset_added_to_cart" : "offset_removed_from_cart",
        { delay: time },
      );
      this.footprint.inCart = active;
    }

    this.shopSystemStrategy.refreshCart();

    this.components.forEach((button) => {
      button.inCart = active;
    });
  }

  setupComponent(entry: AppEntry, target: Element) {
    const { price, totalFootprint, inCart } = this.footprint;
    const component = new ShopComponent({
      target,
      props: {
        type: entry.type!,
        componentProps: {
          price: price || 0,
          totalFootprint: totalFootprint || 0,
          inCart: inCart ?? false,
          isMobile: window.innerWidth < MOBILE_BREAKPOINT,
          updateFootprint: this.updateFootprint.bind(this),
          //@ts-ignore
          ...entry.payload!,
        },
      },
    });

    //TODO: Is there a way to type this at the call site? Using "dispatcherType" as DispatcherEvent does not give type safety on mis-spellings.
    const offsetEvent: DispatcherEvent = "toggleOffset";

    component.$on(offsetEvent, this.toggleOffset.bind(this));

    this.components.push(component);

    window.dispatchEvent(
      new CustomEvent("ComponentRendered", { detail: component }),
    );
  }

  elementMatcher(entry: AppEntry, element: Element) {
    return () => {
      if (entry.position == "BEFORE") {
        return element.previousElementSibling;
      } else {
        return element.nextElementSibling;
      }
    };
  }

  renderElements(entry: AppEntry) {
    // TODO: What is supposed to happen if the selectors are null? Should we just assume they exist in TS and only have them nullable for convenience in the DB?
    // Setup Elements
    // Get the selector and content for the current page.
    // there may be different selectors for mobile and desktop
    const selector =
      window.innerWidth < MOBILE_BREAKPOINT
        ? entry.selectorMobile ?? entry.selector
        : entry.selector;

    if (!selector && !this.hasModal) {
      this.hasModal = true;
      this.setupComponent(entry, document.body);
      return;
    }

    // remove mobile element if not needed anymore e.g. after resize event
    if (window.innerWidth >= MOBILE_BREAKPOINT) {
      const elements = document.querySelectorAll(entry.selectorMobile!);
      elements &&
        elements.forEach((element) => {
          const matchElement = this.elementMatcher(entry, element);
          const prevElement = matchElement() as HTMLElement | null;
          if (prevElement?.classList?.contains("pina-app")) {
            prevElement.remove();
          }
        });
    } else {
      const elements = document.querySelectorAll(entry.selector!);
      elements &&
        elements.forEach((element) => {
          const matchElement = this.elementMatcher(entry, element);
          const prevElement = matchElement() as HTMLElement | null;
          if (prevElement?.classList?.contains("pina-app")) {
            prevElement.remove();
          }
        });
    }

    console.log("looking for selector", selector);
    const waitForAppElementInterval = setInterval(() => {
      // TODO: we could configure different buttons in the backend -> So we can wait for them individually...
      // TODO: Why does this query for all selectors? Is there any case where multiple buttons should be injected?
      const elements = document.querySelectorAll(selector!);
      // adapt from #pina-app zu .pina-app
      let anyFound = false;
      if (elements.length != 0) {
        // Add only if doesn't already contains app TODO: do this via a selector
        elements.forEach((element: Element) => {
          const matchElement = this.elementMatcher(entry, element);
          if (
            !(matchElement() && matchElement()?.classList.contains("pina-app"))
          ) {
            element.insertAdjacentHTML(
              entry.position == "BEFORE" ? "beforebegin" : "afterend",
              entry.content!,
            );

            const target = matchElement();

            if (target) {
              console.log("setting up component");
              this.setupComponent(entry, target);
              anyFound = true;
            }
          }
        });
      }
      if (anyFound) clearInterval(waitForAppElementInterval);
    }, 200);
  }

  observeUrlChange(config: ShopConfig & { appEntries: AppEntry[] }) {
    let oldHref = document.location.href;
    const body = document.querySelector("body");

    const observer = new MutationObserver(() => {
      if (oldHref !== document.location.href) {
        oldHref = document.location.href;

        setTimeout(() => {
          const injectedElements = document.querySelectorAll(
            `#${BADGE_ID}, #${BUTTON_ID}`,
          );
          if (injectedElements.length === 0) {
            config?.appEntries.forEach((entry: any) => {
              this.renderElements(entry);
            });
          }
        }, 200);
      }
    });

    body && observer.observe(body, { childList: true, subtree: true });
  }
}

// Gets the current footprint for the user's cart and refreshes the button.

// catch route changes without new page loads

// Setup
